import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';

interface ArticleDrawerItem {
  id: string;
  section: string;
  title: string;
  summary: string;
  url: string;
  published_at: string;
  feed_name: string;
  verdict: string | null;
  thesis: string | null;
  topics: string[] | null;
  entities: Array<{ id: string; name: string; kind: string; ticker?: string | null }> | null;
  source_count: number;
}

interface ArticleDrawerProps {
  item: ArticleDrawerItem;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  isPaywalled?: boolean;
}

const SECTION_ACCENT: Record<string, string> = {
  news: '#58a6ff',
  research: '#a371f7',
  newsletters: '#3fb950',
  podcasts: '#d29922',
};

function verdictLabel(verdict: string | null): string | null {
  if (!verdict) return null;
  if (verdict === 'must_read') return 'Must Read';
  if (verdict === 'worth_reading') return 'Worth Reading';
  if (verdict === 'skim') return 'Skim';
  if (verdict === 'skip') return 'Skip';
  return verdict;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function entityLabel(entity: { name: string; kind: string; ticker?: string | null }): string {
  if (entity.kind === 'ticker') return `$${entity.ticker ?? entity.name}`;
  return entity.name;
}

export function ArticleDrawer({ item, onClose, onNext, onPrev, isPaywalled }: ArticleDrawerProps) {
  const { supabaseClient } = useAuth();
  const [fullText, setFullText] = useState<string | null>(null);
  const [transcriptExcerpt, setTranscriptExcerpt] = useState<string[]>([]);
  const [transcriptMeta, setTranscriptMeta] = useState<{
    transcript_status: string;
    transcript_source_kind: string | null;
    transcript_source_url: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  // Slide-in animation
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Fetch enriched article text or transcript excerpts
  useEffect(() => {
    if (!supabaseClient || !item) return;
    let cancelled = false;

    setLoading(true);
    setFullText(null);
    setTranscriptExcerpt([]);
    setTranscriptMeta(null);

    const loadBody = async () => {
      try {
        if (item.section === 'podcasts') {
          const { data, error } = await supabaseClient.rpc('get_podcast_transcript_preview', {
            p_content_id: item.id,
            p_max_chunks: 6,
          });

          if (error) throw error;
          if (cancelled) return;

          const row = Array.isArray(data) ? data[0] : null;
          setTranscriptMeta(
            row
              ? {
                  transcript_status: String(row.transcript_status ?? 'none'),
                  transcript_source_kind: null,
                  transcript_source_url:
                    typeof row.transcript_source_url === 'string' ? row.transcript_source_url : null,
                }
              : null
          );
          setTranscriptExcerpt(
            Array.isArray(row?.excerpt_chunks)
              ? (row.excerpt_chunks as unknown[]).filter(
                  (value): value is string => typeof value === 'string'
                )
              : []
          );
          setLoading(false);
          return;
        }

        const { data, error } = await supabaseClient
          .from('content_enrichments')
          .select('full_text')
          .eq('content_id', item.id)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        setFullText((data as { full_text: string | null } | null)?.full_text ?? null);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setLoading(false);
      }
    };

    void loadBody();

    return () => {
      cancelled = true;
    };
  }, [supabaseClient, item]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setVisible(false);
        setTimeout(onClose, 200);
      }
      if ((e.key === 'ArrowDown' || e.key === 'j') && onNext) {
        e.preventDefault();
        onNext();
      }
      if ((e.key === 'ArrowUp' || e.key === 'k') && onPrev) {
        e.preventDefault();
        onPrev();
      }
    },
    [onClose, onNext, onPrev]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close with animation
  function handleClose() {
    setVisible(false);
    setTimeout(onClose, 200);
  }

  const accent = SECTION_ACCENT[item.section] ?? 'var(--accent-primary)';
  const verdict = verdictLabel(item.verdict);
  const topics = item.topics ?? [];
  const entities = item.entities ?? [];

  // Render full text as paragraphs
  function renderBody() {
    if (loading) {
      return (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="skeleton rounded"
              style={{ height: '16px', width: i % 3 === 0 ? '90%' : i % 2 === 0 ? '100%' : '75%' }}
            />
          ))}
        </div>
      );
    }

    if (item.section === 'podcasts') {
      const transcriptSourceUrl = transcriptMeta?.transcript_source_url || item.url;
      const transcriptStatusMessage =
        transcriptMeta?.transcript_status === 'processing'
          ? 'Transcript is being fetched in the background'
          : transcriptMeta?.transcript_status === 'gated'
            ? 'Transcript exists at the source but is not publicly accessible'
            : transcriptMeta?.transcript_status === 'failed'
              ? 'Transcript fetch failed; open the source episode for the full context'
              : 'Transcript not available in the app yet';

      if (transcriptExcerpt.length > 0) {
        return (
          <div className="article-body">
            <p className="type-label uppercase tracking-[0.16em]" style={{ color: 'var(--text-secondary)' }}>
              Transcript excerpt
            </p>
            {transcriptExcerpt.map((para, index) => (
              <p key={index}>{para}</p>
            ))}
            <div
              className="mt-6 p-4 rounded-lg text-center"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
            >
              <p className="type-label mb-2" style={{ color: 'var(--text-secondary)' }}>
                Excerpted from the source transcript
              </p>
              <a
                href={transcriptSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="notion-btn notion-btn-primary type-label"
                style={{ display: 'inline-block', padding: '8px 20px' }}
              >
                Open source &rarr;
              </a>
            </div>
          </div>
        );
      }

      return (
        <div className="article-body">
          {item.summary && <p>{item.summary}</p>}
          {item.thesis && (
            <p className="italic" style={{ color: 'var(--text-tertiary)' }}>
              {item.thesis}
            </p>
          )}
          <div
            className="mt-6 p-4 rounded-lg text-center"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
          >
            <p className="type-label mb-2" style={{ color: 'var(--text-secondary)' }}>
              {transcriptStatusMessage}
            </p>
            <a
              href={transcriptSourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="notion-btn notion-btn-primary type-label"
              style={{ display: 'inline-block', padding: '8px 20px' }}
            >
              Open source &rarr;
            </a>
          </div>
        </div>
      );
    }

    if (fullText) {
      // Split enriched text into paragraphs
      const paragraphs = fullText
        .split(/\n\n+/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      return (
        <div className="article-body">
          {paragraphs.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      );
    }

    // No enrichment: show summary + CTA
    return (
      <div className="article-body">
        {item.summary && <p>{item.summary}</p>}
        {item.thesis && (
          <p className="italic" style={{ color: 'var(--text-tertiary)' }}>
            {item.thesis}
          </p>
        )}
        <div
          className="mt-6 p-4 rounded-lg text-center"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
        >
          <p className="type-label mb-2" style={{ color: 'var(--text-secondary)' }}>
            {isPaywalled ? 'This is a paid source' : 'Full article available at source'}
          </p>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="notion-btn notion-btn-primary type-label"
            style={{ display: 'inline-block', padding: '8px 20px' }}
          >
            Read at source &rarr;
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="article-drawer-overlay"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={handleClose}
      />
      {/* Drawer */}
      <div
        className="article-drawer"
        style={{ transform: visible ? 'translateX(0)' : 'translateX(100%)' }}
      >
        {/* Header */}
        <div className="article-drawer-header">
          <button
            onClick={handleClose}
            className="type-label"
            style={{
              color: 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            &larr; Back to feed
          </button>
          <div className="flex items-center gap-2">
            {onPrev && (
              <button onClick={onPrev} className="thumb-btn" title="Previous article (↑ or k)">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
            )}
            {onNext && (
              <button onClick={onNext} className="thumb-btn" title="Next article (↓ or j)">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="notion-btn notion-btn-secondary type-label"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <span className="hidden sm:inline">Open source</span> &#x2197;
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="article-drawer-content">
          {/* Meta */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="feed-kicker" style={{ color: accent }}>
              {item.section}
            </span>
            {verdict && (
              <span
                className="feed-kicker px-1.5 py-0.5 rounded-full"
                style={{ background: 'rgba(35, 131, 226, 0.12)', color: 'var(--accent-primary)' }}
              >
                {verdict}
              </span>
            )}
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              &middot;
            </span>
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              {item.feed_name}
            </span>
            {isPaywalled && (
              <span
                className="type-caption"
                style={{ color: 'var(--warning)' }}
                title="Paid source"
              >
                {'\uD83D\uDD12'}
              </span>
            )}
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              &middot;
            </span>
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              {timeAgo(item.published_at)}
            </span>
            {item.source_count > 1 && (
              <span
                className="feed-kicker px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
              >
                {item.source_count} sources
              </span>
            )}
          </div>

          {/* Title */}
          <h2 className="type-hero mb-4" style={{ color: 'var(--text-primary)', lineHeight: 1.2 }}>
            {item.title}
          </h2>

          {/* Chips */}
          {(topics.length > 0 || entities.length > 0) && (
            <div className="flex gap-1.5 flex-wrap mb-6">
              {topics.slice(0, 5).map((topic) => (
                <span key={topic} className="feed-chip">
                  {topic}
                </span>
              ))}
              {entities.slice(0, 4).map((entity) => (
                <span key={entity.id} className="feed-chip feed-chip-entity">
                  {entityLabel(entity)}
                </span>
              ))}
            </div>
          )}

          {/* Divider */}
          <hr
            style={{
              border: 'none',
              borderTop: '1px solid var(--border-primary)',
              margin: '0 0 24px',
            }}
          />

          {/* Body */}
          {renderBody()}
        </div>

        {/* Footer */}
        <div className="article-drawer-footer">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="type-label"
            style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
          >
            Read at source &rarr;
          </a>
        </div>
      </div>
    </>
  );
}
